import { useState, type ReactNode } from 'react';
import { Size, t, type BomPickAnswer, type BomPickRow, type BomPickWindow } from '@bms2/shared';
import { NumericInput } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { post, useApi, DEALS_PATH } from './api';
import { RefusalText } from './RefusalText';
import styles from './PickToDealWindow.module.css';

/**
 * 🔨 Relative to the API's own root, never prefixed with /api. The list's own
 * id carries the path (decision 0565); `deal` names the Deal the page was
 * reached from, which the parts go to (decision 3322).
 */
export const BOM_PICKS_PATH = (bomId: string, dealId?: string | null) => `${DEALS_PATH}/boms/${bomId}/picks${dealId ? `?deal=${encodeURIComponent(dealId)}` : ''}`;

/**
 * Pick to Deal (piece 18; decision 3322, T2276): the list's From Stock lines,
 * each with the shelf it is reserved at, what that shelf holds, what is
 * reserved and what has been picked, and a quantity to pick that opens on
 * what the line still needs. 🔒 The Primary issues every line given a
 * quantity to the Deal in one act; the server's refusal, the one above the
 * shelf among them, is drawn inside the frame. 🔨 It holds its quantities in
 * its own state, so the page unmounts it when it closes.
 */
export function PickToDealWindow({ bomId, dealId, apiBaseUrl = '', onCancel, onPicked }: { bomId: string; dealId: string | null; apiBaseUrl?: string; onCancel: () => void; onPicked: (answer: BomPickAnswer) => void }) {
  const read = useApi<BomPickWindow>(BOM_PICKS_PATH(bomId, dealId), apiBaseUrl);
  const picking = read.state.kind === 'ready' ? read.state.data : null;
  /** The quantities typed, by line; a line not typed opens on what it still needs. */
  const [typedQuantities, setTyped] = useState<Record<string, number | null>>({});
  const [error, setError] = useState<ReactNode>(null);
  const quantityOf = (row: BomPickRow) => (row.lineId in typedQuantities ? typedQuantities[row.lineId] ?? null : row.siteId ? row.unpicked : 0);
  const refusal = error ?? picking?.refusal ?? (read.state.kind === 'failed' ? read.state.error : null);

  const submit = async (): Promise<void> => {
    if (!picking || !picking.dealId) return;
    const picks = picking.rows.map((row) => ({ lineId: row.lineId, quantity: quantityOf(row) ?? 0 })).filter((pick) => pick.quantity > 0);
    if (picks.length === 0) return setError(t('deal.bom.pick.nothing'));
    const picked = await post<BomPickAnswer>(BOM_PICKS_PATH(bomId), { dealId: picking.dealId, picks }, apiBaseUrl);
    // 🔒 The server's own sentence, the refusal above the shelf among them, is what the window shows.
    if (!picked.ok) return setError(<RefusalText message={picked.message} body={picked.body} />);
    onPicked(picked.data);
  };

  const pickable = picking !== null && picking.dealId !== null && picking.rows.some((row) => row.siteId !== null && row.unpicked > 0);
  return (
    <FormFrame
      host="window"
      open
      title={t('deal.bom.pick.title')}
      subtitle={picking?.dealName ? `${t('deal.bom.pick.to')} ${picking.dealName}` : t('deal.bom.pick.hint')}
      width="wide"
      onCancel={onCancel}
      dataForm="pick-to-deal"
      typed={Object.keys(typedQuantities).length > 0}
      refusal={refusal}
      onDismissRefusal={() => setError(null)}
      act={{ label: t('deal.bom.pick.act'), onAct: () => submit(), disabled: !pickable, disabledReason: t('deal.bom.pick.nothingToPick') }}
    >
      <div className={styles.rows} data-bom-picks={bomId}>
        {picking && picking.rows.length === 0 ? <p className={styles.empty}>{t('deal.bom.pick.none')}</p> : null}
        {(picking?.rows ?? []).map((row) => (
          <div key={row.lineId} className={styles.row} data-bom-pick-row={row.lineId}>
            <div className={styles.item}>
              <span className={styles.label}>{row.itemLabel}</span>
              <span className={styles.facts}>
                <span data-bom-pick-site={row.siteId ?? ''}>{row.siteName ? `${t('deal.bom.at')} ${row.siteName}` : t('deal.bom.stockSite.none')}</span>
                <span data-bom-pick-on-hand={String(row.onHandAtSite)}>{t('deal.bom.onHand')} {row.onHandAtSite} {row.unitName}</span>
                <span data-bom-pick-reserved={String(row.reserved)}>{t('deal.bom.reserved')} {row.reserved}</span>
                <span data-bom-pick-picked={String(row.picked)}>{t('deal.bom.picked')} {row.picked} {t('deal.bom.pick.of')} {row.quantity}</span>
              </span>
            </div>
            <NumericInput
              size={Size.Small}
              kind="number"
              label={t('deal.bom.pick.quantity')}
              labelHidden
              name={`pick-${row.lineId}`}
              value={quantityOf(row)}
              onChange={(next) => setTyped((was) => ({ ...was, [row.lineId]: next }))}
              disabled={row.siteId === null || row.unpicked <= 0}
            />
          </div>
        ))}
      </div>
    </FormFrame>
  );
}
